import { Box, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoTooltip from 'DesignSystem/DataDisplay/InfoTooltip/InfoTooltip';
import HTMLEditorButton from 'DesignSystem/Formik/HTMLEditorField/HTMLEditorButton';
import InlineStyleControls from 'DesignSystem/Formik/HTMLEditorField/InlineStyleControls';
import LinkDecorator from 'DesignSystem/Formik/HTMLEditorField/LinkDecorator';
import HTMLLinkDialog from 'DesignSystem/Overlays/HTMLLinkDialog/HTMLLinkDialog';
import { DraftEditorCommand, Editor, EditorState, EntityInstance, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import get from 'lodash/get';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import theme from 'styles/theme';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { ELanguage } from 'DesignSystem/types/language.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { useDebouncedCallback } from 'use-debounce';
import { LANGUAGES } from 'utils/language.utils';

interface IHTMLEditorFieldProps {
  value: string;
  name: string;
  charLimit?: number;
  minHeight?: number;
  status?: { lang?: ELanguage };
  placeholder?: string;
  'data-testId'?: string;
  errors?: object[];
  onChange: (name: string, value: string) => void;
}

type TStatus = 'default' | 'error' | 'focused';

const BORDER_COLOR: Record<TStatus, string> = {
  default: theme.custom.colors.lightestBorder,
  error: theme.palette.error.main,
  focused: theme.palette.primary.main,
};

const StyledEditorContainer = styled(Box)<{ 'min-height'?: number; status: TStatus }>(
  // @ts-ignore - Need since overflowWrap type is not allowing the important props (otherwise the maxWidth could overflow if no spaces are included)
  ({ 'min-height': minHeight, status = 'default', theme }) => ({
    position: 'relative',
    background: '#fff',
    border: '1px solid',
    borderColor: BORDER_COLOR[status],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),

    '& .public-DraftEditor-content': {
      minHeight,
      overflowWrap: 'anywhere !important',
    },
  })
);

const ControlContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& .tooltip-url': {
    margin: theme.spacing(0, 0.5),
  },
}));

const CharacterLimit = styled(FormHelperText)<{ visible: Tbooleanish }>(({ visible }) => ({
  position: 'absolute',
  right: 0,
  bottom: -20,
  opacity: visible === 'true' ? 1 : 0,
  transform: visible === 'true' ? 'translateY(0)' : 'translateY(-20px)',
  transition: 'all 0.06s ease-out',
}));

const StyledEditor = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${theme.custom.colors.lightestBorder}`,
  paddingTop: theme.spacing(1),
}));

const Flag = styled('img')<{ visible: Tbooleanish }>(({ visible }) => ({
  position: 'absolute',
  right: 0,
  top: -22,
  borderRadius: 2,
  width: 20,
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(22px)',
  transition: 'all 0.06s ease-out',
}));

/**
 * Use this instead of `HTMLEditor` if you're using useFormik or no form library
 */
const HTMLEditorField: FC<IHTMLEditorFieldProps> = ({
  value: initialValue,
  charLimit,
  placeholder,
  minHeight,
  status,
  name,
  errors,
  onChange,
  ...props
}) => {
  const [focused, setFocused] = useState<boolean>(false);
  const [linkDialog, setLinkDialog] = useState<string | undefined>(undefined);
  const [showURLInput, setShowURLInput] = useState<boolean>(false);
  const makeEditorState = (htmlString: string) => {
    return EditorState.createWithContent(stateFromHTML(htmlString), LinkDecorator);
  };
  const [editorState, setEditorState] = useState(() => makeEditorState(initialValue));
  const lang = status?.lang;

  const currentTextLength = useMemo(
    () => (editorState.getCurrentContent().getPlainText() || '').length,
    [editorState]
  );

  // if text already exists and it is longer than the limit, then the limit is not applied
  const maxChars = useMemo(() => {
    if (charLimit && currentTextLength <= charLimit) {
      return charLimit;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charLimit]);

  useEffect(() => {
    if (focused) return;

    setEditorState(makeEditorState(initialValue));
  }, [initialValue, focused]);

  const [touched, setTouched] = useState<boolean>(false);
  const fieldError: string | undefined = errors && get(errors, name);
  const showError = touched && !!fieldError;

  const fieldStatus = useMemo(() => {
    if (showError) {
      return 'error';
    }
    if (focused) {
      return 'focused';
    }
    return 'default';
  }, [showError, focused]);

  const handleChange = useCallback(
    (state: EditorState) => {
      const contentState = state.getCurrentContent();
      const oldContent = editorState.getCurrentContent();
      if (
        !maxChars ||
        contentState <= oldContent ||
        (contentState.getPlainText() || '').length <= maxChars
      ) {
        setEditorState(state);
        debouncedSetFieldValue.callback(state);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState, maxChars, charLimit]
  );

  const debouncedSetFieldValue = useDebouncedCallback(state => {
    const options = {
      entityStyleFn: (entity: EntityInstance) => {
        const entityType = entity.getType().toLowerCase();
        if (entityType === 'link') {
          const data = entity.getData();
          return {
            element: 'a',
            attributes: {
              href: data.url,
              target: '_blank',
            },
          };
        }
      },
    };
    onChange(name, stateToHTML(state.getCurrentContent(), options));
  }, 250);

  const handleKeyCommand = (command: DraftEditorCommand, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleChange(newState);

      return 'handled';
    }

    return 'not-handled';
  };

  const toggleInlineStyle = (inlineStyle: string) => {
    handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const promptForLink = () => {
    const selection = editorState.getSelection();
    const anchorKey = selection.getAnchorKey();
    const currentContent = editorState.getCurrentContent();
    const currentContentBlock = currentContent.getBlockForKey(anchorKey);
    const start = selection.getStartOffset();
    const end = selection.getEndOffset();
    const selected = currentContentBlock.getText().slice(start, end);
    // openDialog({ type: 'HTML_LINK', props: { linkText: selected, onSubmit: handleSubmitLink } });
    setLinkDialog(selected);
    if (!selection.isCollapsed()) {
      setShowURLInput(true);
    }
  };

  const handleSubmitLink = ({ url }: { url?: string }) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    let nextEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    nextEditorState = RichUtils.toggleLink(
      nextEditorState,
      nextEditorState.getSelection(),
      entityKey
    );
    setShowURLInput(false);
    setEditorState(nextEditorState);
  };

  const getLengthOfSelectedText = () => {
    const currentSelection = editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();

    let length = 0;

    if (!isCollapsed) {
      const currentContent = editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startBlockTextLength = startBlock.getLength();
      const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);
      if (isStartAndEndBlockAreTheSame) {
        length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }

          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  };

  const checkCharacterLimit = useCallback(
    (chars: string) => {
      const selectedText = getLengthOfSelectedText() || 0;
      const totalLength =
        editorState.getCurrentContent().getPlainText().length + chars.length - selectedText;

      if (!charLimit) {
        return 'not-handled';
      }

      // Returning 'handled' causes the default behavior of the beforeInput event to be prevented
      return totalLength > charLimit ? 'handled' : 'not-handled';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [charLimit]
  );

  const handleFocus = useCallback(() => setFocused(true), [setFocused]);

  const handleBlur = useCallback(() => {
    if (!touched) {
      setTouched(true);
    }
    if (!showURLInput) {
      setFocused(false);
    }
  }, [showURLInput, touched, setFocused]);

  return (
    <>
      {/* TODO: Remove this and use the dialog context instead as soon as the import issue is resolved */}
      {!!linkDialog && (
        <HTMLLinkDialog
          open={!!linkDialog}
          linkText={linkDialog}
          onSubmit={handleSubmitLink}
          onClose={() => setLinkDialog(undefined)}
        />
      )}
      <StyledEditorContainer
        status={fieldStatus}
        minHeight={minHeight}
        data-testid={props['data-testId'] || ''}
      >
        <ControlContainer>
          <InlineStyleControls editorState={editorState} onToggle={toggleInlineStyle} />
          <HTMLEditorButton label="Add Link" style={undefined} onToggle={promptForLink} />
          <Box mb={1} ml={0.2}>
            <InfoTooltip
              className="tooltip-url"
              size={EAvailableSizes.SMALL}
              text="To create a clickable url link please mark the desired text."
            />
          </Box>
        </ControlContainer>
        <StyledEditor>
          <Editor
            handleBeforeInput={
              maxChars !== undefined && maxChars > 0 ? undefined : checkCharacterLimit
            }
            handlePastedText={
              maxChars !== undefined && maxChars > 0 ? undefined : checkCharacterLimit
            }
            onFocus={handleFocus}
            onBlur={handleBlur}
            editorState={editorState}
            onChange={handleChange}
            handleKeyCommand={handleKeyCommand}
            placeholder={placeholder}
          />
        </StyledEditor>
        {lang && <Flag visible={Booleanish(focused)} src={LANGUAGES[lang].flag} />}
        {maxChars && (
          <CharacterLimit
            visible={Booleanish(focused)}
          >{`${currentTextLength}/${charLimit}`}</CharacterLimit>
        )}
      </StyledEditorContainer>
      {showError && <FormHelperText error>{fieldError}</FormHelperText>}
    </>
  );
};

export default HTMLEditorField;
